import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

const QuoteToInvoiceModal = ({ show, onHide, quotation, onSuccess }) => {
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');
  const { showInlineNotice } = useInlineNotice();

  const handleConvert = async () => {
    try {
      setIsConverting(true);
      setError('');

      const response = await axios.post(
        `${API_BASE_URL}/documents/${quotation._id}/quote-to-invoice`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error converting quotation:', err);
      const message = err.response?.data?.error || 'Failed to convert quotation to invoice';
      setError(message);
      showInlineNotice(message, {
        scope: 'quotations',
        type: 'error'
      });
    } finally {
      setIsConverting(false);
    }
  };

  if (!quotation) return null;

  const totalProducts = quotation.products ? quotation.products.length : 0;
  const formatCurrency = (value) => (
    formatCurrencyBySystem(Number(value || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    })
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Convert Quotation to Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <div className="quote-preview">
          <h6 className="mb-3">Quotation Details</h6>

          <div className="row mb-3">
            <div className="col-md-6">
              <p className="mb-2">
                <strong>Quotation #:</strong> {quotation.documentNumber}
              </p>
              <p className="mb-2">
                <strong>Customer:</strong> {quotation.customer}
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-2">
                <strong>Date:</strong> {new Date(quotation.createdAt).toLocaleDateString('en-IN')}
              </p>
              <p className="mb-2">
                <strong>Total:</strong> {formatCurrency(quotation.total)}
              </p>
            </div>
          </div>

          <div className="mb-3">
            <p className="mb-2">
              <strong>Products:</strong> {totalProducts} item(s)
            </p>
            {quotation.products && quotation.products.length > 0 && (
              <ul className="list-unstyled small" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {quotation.products.map((product, idx) => (
                  <li key={idx} className="mb-1 pb-1" style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {product.description || product.name} × {product.quantity} @ {formatCurrency(product.price)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Alert variant="info" className="mb-0">
            <strong>Note:</strong> This will create a new Invoice with the same customer, products, and total. The quotation will remain unchanged.
          </Alert>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isConverting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConvert}
          disabled={isConverting}
        >
          {isConverting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Converting...
            </>
          ) : (
            'Convert to Invoice'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuoteToInvoiceModal;
